import { scrollbarStyle } from '@/shared/styles'
import { css, darken, lighten, styled, Theme } from '@mui/material'
import { DataGridPremium } from '@mui/x-data-grid-premium'

export const StyledDataGrid = styled(DataGridPremium)`
  & .MuiDataGrid-row {
    font-weight: 400;
  }
  
  background-color: ${({ theme }) => theme.palette.custom.gridTable.background};
  
  & .MuiDataGrid-columnHeader {
    background-color: ${({ theme }) => theme.palette.custom.gridTable.columnHeaderBackground};
    border-bottom: 1px solid ${({ theme }) => theme.palette.custom.gridTable.borderColor};
    border-right: 1px solid ${({ theme }) => theme.palette.custom.gridTable.borderColor};
  }

  & .MuiDataGrid-columnHeader--pinnedLeft {
    border-right: none;
    box-shadow: 1px 0px 0px ${({ theme }) => theme.palette.custom.gridTable.borderColor};
  }
  
  & .MuiDataGrid-cell {
    border-right: 1px solid ${({ theme }) => theme.palette.custom.gridTable.borderColor};
    padding-left: 0 6px;
  }

  & .MuiDataGrid-cell--editing {
    overflow: visible;
  }

  & .MuiDataGrid-cell--pinnedLeft {
    border-right: none;
    background-color: ${({ theme }) => theme.palette.custom.gridTable.columnHeaderBackground};
    box-shadow: 1px 0px 0px ${({ theme }) => theme.palette.custom.gridTable.borderColor};
  }

  & .MuiDataGrid-row {
    border-bottom: 1px solid ${({ theme }) => theme.palette.custom.gridTable.borderColor};
  }

  & .MuiDataGrid-filler--pinnedLeft {
    display: none;
  }

  & .MuiDataGrid-scrollbar {
    ${scrollbarStyle}
  }

  & .MuiCheckbox-root.Mui-disabled {
    display: none;
  }

  & .MuiDataGrid-toolbarContainer {
    padding: 0 4px;
  }

  ${({ theme }) => getGridRowTheme(theme)}
`

const getBackgroundColor = (color: string, mode: string) =>
  mode === 'dark' ? darken(color, 0.7) : lighten(color, 0.8)

const getHoverBackgroundColor = (color: string, mode: string) =>
  mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6)

const getSelectedBackgroundColor = (color: string, mode: string) =>
  mode === 'dark' ? darken(color, 0.5) : lighten(color, 0.5)

const getSelectedHoverBackgroundColor = (color: string, mode: string) =>
  mode === 'dark' ? darken(color, 0.4) : lighten(color, 0.4)

/**
 * The following css grid row styles are the default configuration.
 *    These styles can be extended and included in StyledDataGrid
 *    to support different client's need based on their moniker
 */
const getGridRowTheme = (theme: Theme, moniker: string = 'orcoda') => {
  return css`
    & .${moniker}-theme--textHighlight {
      color: ${theme.palette.custom.gridTable.textHighlight};
    }
    & .${moniker}-theme--info {
      background-color: ${getBackgroundColor(theme.palette.info.main, theme.palette.mode)};
      & .MuiDataGrid-cell--pinnedLeft {
        background-color: inherit;
      }
      &:hover {
        background-color: ${getHoverBackgroundColor(theme.palette.info.main, theme.palette.mode)};
        & .MuiDataGrid-cell--pinnedLeft {
          background-color: inherit !important;
        }
      }
      &.Mui-selected {
        background-color: ${getSelectedBackgroundColor(theme.palette.info.main, theme.palette.mode)};
        &:hover {
          background-color: ${getSelectedHoverBackgroundColor(theme.palette.info.main, theme.palette.mode)};
        }
      }
    }
  
    & .${moniker}-theme--success {
      background-color: ${getBackgroundColor(theme.palette.success.main, theme.palette.mode)};
      & .MuiDataGrid-cell--pinnedLeft {
        background-color: inherit;
      }
      &:hover {
        background-color: ${getHoverBackgroundColor(theme.palette.success.main, theme.palette.mode)};
        & .MuiDataGrid-cell--pinnedLeft {
          background-color: inherit !important;
        }
      }
      &.Mui-selected {
        background-color: ${getSelectedBackgroundColor(theme.palette.success.main, theme.palette.mode)};
        &:hover {
          background-color: ${getSelectedHoverBackgroundColor(theme.palette.success.main, theme.palette.mode)};
        }
      }
    }
  
    & .${moniker}-theme--warning {
      background-color: ${getBackgroundColor(theme.palette.warning.main, theme.palette.mode)};
      & .MuiDataGrid-cell--pinnedLeft {
        background-color: inherit;
      }
      &:hover {
        background-color: ${getHoverBackgroundColor(theme.palette.warning.main, theme.palette.mode)};
        & .MuiDataGrid-cell--pinnedLeft {
          background-color: inherit !important;
        }
      }
      &.Mui-selected {
        background-color: ${getSelectedBackgroundColor(theme.palette.warning.main, theme.palette.mode)};
        &:hover {
          background-color: ${getSelectedHoverBackgroundColor(theme.palette.warning.main, theme.palette.mode)};
        }
      }
    }
  
    & .${moniker}-theme--error {
      background-color: ${getBackgroundColor(theme.palette.error.main, theme.palette.mode)};
      & .MuiDataGrid-cell--pinnedLeft {
        background-color: inherit;
      }
      &:hover {
        background-color: ${getHoverBackgroundColor(theme.palette.error.main, theme.palette.mode)};
        & .MuiDataGrid-cell--pinnedLeft {
          background-color: inherit !important;
        }
      }
      &.Mui-selected {
        background-color: ${getSelectedBackgroundColor(theme.palette.error.main, theme.palette.mode)};
        &:hover {
          background-color: ${getSelectedHoverBackgroundColor(theme.palette.error.main, theme.palette.mode)};
        }
      }
    }
    
    & .${moniker}-theme--primary {
      background-color: ${getBackgroundColor(theme.palette.primary.main, theme.palette.mode)};
      & .MuiDataGrid-cell--pinnedLeft {
        background-color: inherit;
      }
      &:hover {
        background-color: ${getHoverBackgroundColor(theme.palette.primary.main, theme.palette.mode)};
        & .MuiDataGrid-cell--pinnedLeft {
          background-color: inherit !important;
        }
      }
      &.Mui-selected {
        background-color: ${getSelectedBackgroundColor(theme.palette.primary.main, theme.palette.mode)};
        &:hover {
          background-color: ${getSelectedHoverBackgroundColor(theme.palette.primary.main, theme.palette.mode)};
        }
      }
    }
    
    & .${moniker}-theme--secondary {
      background-color: ${getBackgroundColor(theme.palette.secondary.main, theme.palette.mode)};
      & .MuiDataGrid-cell--pinnedLeft {
        background-color: inherit;
      }
      &:hover {
        background-color: ${getHoverBackgroundColor(theme.palette.secondary.main, theme.palette.mode)};
        .MuiDataGrid-cell--pinnedLeft {
          background-color: inherit !important;
        }
      }
      &.Mui-selected {
        background-color: ${getSelectedBackgroundColor(theme.palette.secondary.main, theme.palette.mode)};
        &:hover {
          background-color: ${getSelectedHoverBackgroundColor(theme.palette.secondary.main, theme.palette.mode)};
        }
      }
    }
  `
}